import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useRef,
} from 'react';
import { Toast, type ToastProps } from '../components/atoms/Toast';

interface ToastContextType {
  showToast: (toast: Omit<ToastProps, 'id' | 'onClose'>) => string;
  hideToast: (id: string) => void;
  showSuccess: (message: string, options?: Partial<ToastProps>) => string;
  showError: (message: string, options?: Partial<ToastProps>) => string;
  showWarning: (message: string, options?: Partial<ToastProps>) => string;
  showInfo: (message: string, options?: Partial<ToastProps>) => string;
}

const ToastContext = createContext<ToastContextType | undefined>(undefined);

export const useToast = () => {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return context;
};

interface ActiveToast extends ToastProps {
  id: string;
}

export const ToastProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [toasts, setToasts] = useState<ActiveToast[]>([]);
  const idCounter = useRef(0);

  const hideToast = useCallback((id: string) => {
    setToasts((prev) => prev.filter((toast) => toast.id !== id));
  }, []);

  const showToast = useCallback(
    (toast: Omit<ToastProps, 'id' | 'onClose'>): string => {
      const id = `toast-${++idCounter.current}`;
      const newToast: ActiveToast = {
        ...toast,
        id,
        onClose: (toastId: string) => hideToast(toastId),
      };

      setToasts((prev) => [...prev, newToast]);
      return id;
    },
    [hideToast]
  );

  const showSuccess = useCallback(
    (message: string, options: Partial<ToastProps> = {}) => {
      return showToast({
        message,
        type: 'success',
        duration: 4000,
        ...options,
      });
    },
    [showToast]
  );

  const showError = useCallback(
    (message: string, options: Partial<ToastProps> = {}) => {
      return showToast({
        message,
        type: 'error',
        duration: 6000,
        ...options,
      });
    },
    [showToast]
  );

  const showWarning = useCallback(
    (message: string, options: Partial<ToastProps> = {}) => {
      return showToast({
        message,
        type: 'warning',
        duration: 5000,
        ...options,
      });
    },
    [showToast]
  );

  const showInfo = useCallback(
    (message: string, options: Partial<ToastProps> = {}) => {
      return showToast({
        message,
        type: 'info',
        duration: 4000,
        ...options,
      });
    },
    [showToast]
  );

  const contextValue: ToastContextType = {
    showToast,
    hideToast,
    showSuccess,
    showError,
    showWarning,
    showInfo,
  };

  return (
    <ToastContext.Provider value={contextValue}>
      {children}
      {toasts.map((toast) => (
        <Toast key={toast.id} {...toast} />
      ))}
    </ToastContext.Provider>
  );
};
